import { Injectable, OnDestroy } from '@angular/core';
import { concatMap, Observable, of, Subject, tap } from 'rxjs';
import { ActionDispatcher } from './action-dispatcher';
import { ActionRequest } from './action-request';
import { ActionEvent } from './action-response';

/**
 * Service for dispatching `ActionRequest`s.
 */
@Injectable()
export class ActionService implements OnDestroy {
  private _actionEvents = new Subject<ActionEvent<unknown>>();

  constructor(private dispatcher: ActionDispatcher) {}

  ngOnDestroy(): void {
    this._actionEvents.complete();
  }

  /**
   * Stream of all action events
   */
  public get actionEvents$(): Observable<ActionEvent<unknown>> {
    return this._actionEvents.asObservable();
  }

  /**
   * Dispatches an action with the given name and arguments
   *
   * @param action
   * The action that should be dispatched
   *
   * @param args
   * The arguments with which the action should be dispatched
   *
   * @example
   * service.dispatchAction('some-action', 'some-argument')
   * .subscribe(event => console.log('action event emitted'))
   *
   * @example
   * // If you are only interested in the final response, use
   * service.dispatchAction('some-action', 'some-argument')
   * .pipe(filter(isActionResponse))
   * .subscribe(res => console.log('action response emitted'))
   */
  public dispatchAction<T>(
    action: string,
    args: T,
  ): Observable<ActionEvent<unknown>> {
    // First, we start of by preparing the action request
    const actionRequest = new ActionRequest(action, args);

    // Run the dispatcher (which includes all handlers) inside a `concatMap()`.
    // This way, the dispatcher runs inside an Observable chain,
    // which causes handlers to be re-run on every subscription
    // (this also makes retries re-run handlers).
    const events$ = of(actionRequest).pipe(
      concatMap((req) => this.dispatcher.dispatch(req)),
    );

    // Emit all events on the shared stream of action events as well
    return events$.pipe(tap((e) => this._actionEvents.next(e)));
  }

  /**
   * @param action The action ID
   *
   * @returns
   * An `Observable` that emits with a boolean value that is `true`,
   * if the `action` can be executed.
   */
  public isExecuteable(action: string): Observable<boolean> {
    return of(action).pipe(
      concatMap((actionId) => this.dispatcher.isExecuteable(actionId)),
    );
  }
}
